<script setup lang="ts">
/**
 * Shown when the storefront could not be loaded for a reason that is expected
 * to pass — the API throttling us, a 5xx, a timeout. These used to land on the
 * 404 view, which told visitors the salon did not exist.
 */
const { t } = useI18n();

const emits = defineEmits<
  (e: "handleRetry" | "handleClearError") => void
>();
</script>

<template>
  <div
    class="flex flex-col items-center justify-center h-screen bg-cover bg-top bg-[url('/errors/error-background.svg')] p-10"
  >
    <div class="flex flex-col justify-center items-center max-w-md">
      <div class="text-center mb-6 lg:mb-7.5">
        <h1
          class="text-white text-3xl xs:text-4xl lg:text-[40px] font-medium block leading-tight ltr:first-letter:uppercase"
        >
          {{ t("errorPages.errorUnavailable.title") }}
        </h1>
        <!-- A block with its own top margin: as an inline span straight after
             a 40px heading the description collided with its descenders. -->
        <p class="text-white/80 text-sm xs:text-base mt-3 leading-relaxed">
          {{ t("errorPages.errorUnavailable.desc") }}
        </p>
      </div>

      <div class="flex flex-col xs:flex-row items-center gap-x-4 gap-y-3">
        <UButton
          size="xl"
          class="text-white bg-dark-purple bg-[length:200%_auto] bg-gradient-to-r from-dark-purple to-furious-purple ring-furious-purple"
          @click="emits('handleRetry')"
        >
          {{ t("label.retry") }}
        </UButton>

        <!--
          Deliberately not a `UButton variant="link"`: that variant colours
          itself from the primary palette on hover, which on this navy ground
          turned the label almost invisible. The secondary action is a plain
          link instead, so both states are chosen for this background.
        -->
        <button
          type="button"
          class="text-white/75 text-sm font-medium underline decoration-white/30 underline-offset-4 transition-colors duration-200 hover:text-white hover:decoration-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white rounded-sm"
          @click="emits('handleClearError')"
        >
          {{ t("label.back_to_home") }}
        </button>
      </div>
    </div>
  </div>
</template>
